import * as Sentry from '@sentry/react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'

import { DashboardLayout } from './components/navigation/DashboardLayout'
import { useAuth } from './hooks/useAuth'
import { MeasurementsProvider } from './hooks/useMeasurements'
import { AdminReportsPage } from './pages/AdminReportsPage'
import { AskPage } from './pages/AskPage'
import { ProfilePage } from './pages/ProfilePage'
import { LoginPage } from './pages/LoginPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { DashboardUsersPage } from './pages/DashboardUsersPage'
import { MobileUsersPage } from './pages/MobileUsersPage'
import { MobileUserDetailPage } from './pages/MobileUserDetailPage'
import { TourAdminWorkspacePage } from './pages/TourAdminWorkspacePage'
import { TourManagementPage } from './pages/TourManagementPage'
import { TourOperatorsAdminPage } from './pages/TourOperatorsAdminPage'
import { PopularHikesAdminPage } from './pages/PopularHikesAdminPage'
import { TestimonialsAdminPage } from './pages/TestimonialsAdminPage'
import { ExercisesAdminPage } from './pages/ExercisesAdminPage'
import { BadgesAdminPage } from './pages/BadgesAdminPage'
import { CampaignMessagesPage } from './pages/CampaignMessagesPage'
import { CampaignPollResultsPage } from './pages/CampaignPollResultsPage'
import { ChallengesAdminPage } from './pages/ChallengesAdminPage'
import { ExerciseVideosPage } from './pages/ExerciseVideosPage'
import { ExerciseTrackerInstructionsPage } from './pages/ExerciseTrackerInstructionsPage'
import { SystemPromptsPage } from './pages/SystemPromptsPage'
import { FitnessHikeTargetsPage } from './pages/FitnessHikeTargetsPage'
import { RevenuePage } from './pages/RevenuePage'
import { TrainingPlanTestPage } from './pages/TrainingPlanTestPage'
import { PlanAlgorithmPage } from './pages/PlanAlgorithmPage'
import { HikeStrongIndexConfigPage } from './pages/HikeStrongIndexConfigPage'
import { DocsPage } from './pages/DocsPage'
import { SupportQueuePage } from './pages/SupportQueuePage'
import { SupportTicketPage } from './pages/SupportTicketPage'
import { SupportModerationPage } from './pages/SupportModerationPage'
import { ModerationCaseDetailPage } from './pages/ModerationCaseDetailPage'
import { StringTablesPage } from './pages/StringTablesPage'
import { StatusPage } from './pages/StatusPage'
import { SocialGroupsAdminPage } from './pages/SocialGroupsAdminPage'
import { FeatureFlagsPage } from './pages/FeatureFlagsPage'
import { DefaultProfilePicturesPage } from './pages/DefaultProfilePicturesPage'
import { AiFeedbackPage } from './pages/AiFeedbackPage'
import { ApiKeysPage } from './pages/ApiKeysPage'
import { TrackingDiagnosticsPage } from './pages/TrackingDiagnosticsPage'
import { ApplicationHealthPage } from './pages/ApplicationHealthPage'
import { ContentPromotionPage } from './pages/ContentPromotionPage'
import { AccountDeletionPage } from './pages/AccountDeletionPage'

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

const AuthLoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center bg-muted">
    <p className="text-sm text-muted-foreground">Checking authentication…</p>
  </div>
)

const RequireAuth = () => {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return <AuthLoadingScreen />
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  return <Outlet />
}

const LoginRoute = ({ defaultPath }: { defaultPath: string }) => {
  const { user, loading } = useAuth()

  if (loading) {
    return <AuthLoadingScreen />
  }

  return user ? <Navigate to={defaultPath} replace /> : <LoginPage />
}

const WithMeasurements = () => (
  <MeasurementsProvider>
    <Outlet />
  </MeasurementsProvider>
)

const RequirePlatformAdmin = () => {
  const { user } = useAuth()
  return user?.role === 'Admin' ? <Outlet /> : <Navigate to="/" replace />
}

const App = () => {
  const { user } = useAuth()
  const role = user?.role
  const defaultPath =
    role === 'TourAdmin' ? '/touradmin' :
    role === 'SupportAgent' || role === 'SupportAdmin' ? '/support' :
    role === 'Guest' ? '/profile' :
    '/users/dashboard'

  return (
    <SentryRoutes>
      <Route path="/login" element={<LoginRoute defaultPath={defaultPath} />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/account-deletion" element={<AccountDeletionPage />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route element={<RequireAuth />}>
        <Route element={<WithMeasurements />}>
          <Route element={<DashboardLayout />}>
            <Route index element={<Navigate to={defaultPath} replace />} />
            <Route path="/users/dashboard" element={<DashboardUsersPage />} />
            <Route path="/support" element={<SupportQueuePage />} />
            <Route path="/support/:ticketId" element={<SupportTicketPage />} />
            <Route path="/support/moderation" element={<SupportModerationPage />} />
            <Route path="/support/moderation/:caseId" element={<ModerationCaseDetailPage />} />
            <Route path="/ai-feedback" element={<AiFeedbackPage />} />
            <Route path="/users/mobile" element={<MobileUsersPage />} />
            <Route path="/users/mobile/:userId" element={<MobileUserDetailPage />} />
            <Route path="/users" element={<Navigate to={defaultPath} replace />} />
            <Route path="/tours" element={<TourManagementPage />} />
            <Route path="/operators" element={<TourOperatorsAdminPage />} />
            <Route path="/popular-hikes" element={<PopularHikesAdminPage />} />
            <Route path="/content-promotion" element={<ContentPromotionPage />} />
            <Route path="/social/groups" element={<SocialGroupsAdminPage />} />
            <Route path="/fitness-hikes" element={<FitnessHikeTargetsPage />} />
            <Route path="/testimonials" element={<TestimonialsAdminPage />} />
            <Route path="/exercises" element={<ExercisesAdminPage />} />
            <Route path="/exercise-videos" element={<ExerciseVideosPage />} />
            <Route path="/exercise-tracker-instructions" element={<ExerciseTrackerInstructionsPage />} />
            <Route path="/badges" element={<BadgesAdminPage />} />
            <Route path="/campaign-messages" element={<CampaignMessagesPage />} />
            <Route path="/campaign-polls" element={<CampaignPollResultsPage />} />
            <Route path="/challenges" element={<ChallengesAdminPage />} />
            <Route path="/system-prompts" element={<SystemPromptsPage />} />
            <Route path="/default-profile-pictures" element={<DefaultProfilePicturesPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/string-tables" element={<StringTablesPage />} />
            <Route path="/system/api-keys" element={<ApiKeysPage />} />
            <Route path="/system/tracking-diagnostics" element={<TrackingDiagnosticsPage />} />
            <Route element={<RequirePlatformAdmin />}>
              <Route path="/reports" element={<AdminReportsPage />} />
              <Route path="/reports/:reportId" element={<AdminReportsPage />} />
              <Route path="/system/application-health" element={<ApplicationHealthPage />} />
            </Route>
            <Route path="/feature-flags" element={<FeatureFlagsPage />} />
            <Route path="/revenue" element={<RevenuePage />} />
            <Route path="/pricing" element={<Navigate to="/revenue" replace />} />
            <Route path="/plan-tester" element={<TrainingPlanTestPage />} />
            <Route path="/plan-algorithm" element={<PlanAlgorithmPage />} />
            <Route path="/plan-config" element={<Navigate to="/plan-algorithm" replace />} />
            <Route path="/hsi-config" element={<HikeStrongIndexConfigPage />} />
            <Route path="/milestone-config" element={<Navigate to="/plan-algorithm?tab=milestones" replace />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/ask" element={<AskPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/touradmin" element={<TourAdminWorkspacePage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to={defaultPath} replace />} />
    </SentryRoutes>
  )
}

export default App
